import { Fragment, useMemo, useState } from 'react';
import { Segmented, Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, shortDate, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface Part {
  revenue: number;
  cost: number;
  items: number;
  costMissing: number;
}
interface SdfRow {
  id: number;
  name: string;
  active: boolean;
  isParent: boolean;
  /** The picked company this row is, or is under. */
  parentId: number;
  /** Companies rolled into this row when comparing picks. */
  rolledUp?: number;
  /** What this client was invoiced in the period (incl. tax, dated by
   *  invoice, voided ones left out) — what the gross margin runs on. */
  invoiced: number;
  invoices: number;
  labor: Part & { hours: number; billableHours: number };
  recurring: Part;
  products: Part;
}
interface SdfPayload {
  parents: { id: number; name: string }[];
  items: number;
  costMissing: number;
  /** Invoices dated in the period for these companies, with tax, voided ones
   *  left out. The headline figure; posted revenue sits under it. */
  invoiced: number;
  invoices: number;
  rows: SdfRow[];
  fetchedUtc: string;
}

type Range = 'month' | 'lastMonth' | 'quarter' | 'ytd' | '12m' | 'custom';
const RANGES: { id: Range; label: string }[] = [
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'quarter', label: 'Last 3 months' },
  { id: 'ytd', label: 'This year' },
  { id: '12m', label: '12 months' },
  { id: 'custom', label: 'Custom' },
];

/** First day and last day (inclusive) of a range, as local YYYY-MM-DD. */
function rangeDays(range: Exclude<Range, 'custom'>): [string, string] {
  const d = new Date();
  const first = (y: number, m: number) => toLocalDateString(new Date(y, m, 1));
  const last = (y: number, m: number) => toLocalDateString(new Date(y, m + 1, 0));
  switch (range) {
    case 'month':
      return [first(d.getFullYear(), d.getMonth()), todayLocal()];
    case 'lastMonth':
      return [first(d.getFullYear(), d.getMonth() - 1), last(d.getFullYear(), d.getMonth() - 1)];
    case 'quarter':
      return [first(d.getFullYear(), d.getMonth() - 2), todayLocal()];
    case 'ytd':
      return [`${d.getFullYear()}-01-01`, todayLocal()];
    case '12m': {
      const s = new Date(d);
      s.setMonth(s.getMonth() - 12);
      s.setDate(s.getDate() + 1);
      return [toLocalDateString(s), todayLocal()];
    }
  }
}

const money = (v: number) => formatNumber(v, 'currency');
const pctOf = (part: number, whole: number) => (whole ? `${((part / whole) * 100).toFixed(1)}%` : '—');
const perHour = (v: number, hours: number) => (hours ? money(v / hours) : '—');

/** Every figure for a row, worked out in one place so the table, the totals
 *  and the CSV say the same thing.
 *
 *  The gross margin is what was INVOICED less what it cost. Posted revenue is
 *  still here — it is the only revenue that can be split into labor, recurring
 *  and products, so the group columns and their margins run on it — but the
 *  bottom line is the money that actually went out on invoices. */
function figures(r: Pick<SdfRow, 'labor' | 'recurring' | 'products' | 'invoiced'>) {
  const posted = r.labor.revenue + r.recurring.revenue + r.products.revenue;
  const cost = r.labor.cost + r.recurring.cost + r.products.cost;
  return {
    posted,
    invoiced: r.invoiced,
    cost,
    margin: r.invoiced - cost,
    postedMargin: posted - cost,
    laborMargin: r.labor.revenue - r.labor.cost,
    productsMargin: r.products.revenue - r.products.cost,
  };
}

interface SdfItem {
  id: number;
  companyId: number;
  date: string;
  group: 'labor' | 'recurring' | 'products';
  type: string;
  name: string | null;
  description: string | null;
  quantity: number;
  rate: number | null;
  revenue: number;
  cost: number | null;
  invoiceId: number | null;
}

const GROUPS: { id: SdfItem['group']; label: string }[] = [
  { id: 'labor', label: 'Labor' },
  { id: 'recurring', label: 'Recurring' },
  { id: 'products', label: 'Products & expenses' },
];

/** The billing line items behind one row, by group, read when the row is
 *  opened. `companyName` is passed when the row adds up several companies,
 *  so each item says which one it was billed to. */
function SdfItems({ companyIds, companyName, from, to }: { companyIds: number[]; companyName?: (id: number) => string; from: string; to: string }) {
  const items = useHeldResource<SdfItem[]>(`/reports/sdf/items?${new URLSearchParams({ companyIds: companyIds.join(','), from, to })}`);
  if (items.error) return <ErrorBanner message={items.error} />;
  if (!items.data) return <Loading label="Reading the line items from Autotask" />;
  if (items.data.length === 0) return <p className="xs muted">Nothing billed in this period.</p>;
  return (
    <table className="table sdf-items">
      <thead>
        <tr>
          <th>Date</th>
          {companyName && <th>Company</th>}
          <th>Type</th>
          <th>Item</th>
          <th>Description</th>
          <th className="num">Qty</th>
          <th className="num">Rate</th>
          <th className="num">Revenue</th>
          <th className="num">Cost</th>
          <th className="num">Margin</th>
        </tr>
      </thead>
      {GROUPS.map((g) => {
        const list = items.data!.filter((it) => it.group === g.id).sort((a, b) => a.date.localeCompare(b.date) || b.revenue - a.revenue);
        if (list.length === 0) return null;
        const revenue = list.reduce((n, it) => n + it.revenue, 0);
        const cost = list.reduce((n, it) => n + (it.cost ?? 0), 0);
        return (
          <tbody key={g.id}>
            {list.map((it) => (
              <tr key={it.id}>
                <td>{shortDate(it.date)}</td>
                {companyName && <td>{companyName(it.companyId)}</td>}
                <td>{it.type}</td>
                <td>{it.name ?? '—'}</td>
                <td className="sdf-desc" title={it.description ?? undefined}>{it.description ?? ''}</td>
                <td className="num">{num(it.quantity, 2)}</td>
                <td className="num">{it.rate === null ? '—' : `$${num(it.rate, 2)}`}</td>
                <td className="num">{money(it.revenue)}</td>
                <td className="num">{it.cost === null ? <span className="muted">not readable</span> : money(it.cost)}</td>
                <td className="num">{it.cost === null ? '—' : money(it.revenue - it.cost)}</td>
              </tr>
            ))}
            <tr className="sdf-subtotal">
              <td colSpan={companyName ? 7 : 6}>
                {g.label} · {list.length} {list.length === 1 ? 'item' : 'items'}
              </td>
              <td className="num">{money(revenue)}</td>
              <td className="num">{money(cost)}</td>
              <td className="num">{money(revenue - cost)}</td>
            </tr>
          </tbody>
        );
      })}
    </table>
  );
}

/** Adds up rows into one — a picked company and everything under it. */
function rollUp(base: SdfRow, rows: SdfRow[]): SdfRow {
  const part = (k: 'recurring' | 'products'): Part => ({
    revenue: rows.reduce((n, r) => n + r[k].revenue, 0),
    cost: rows.reduce((n, r) => n + r[k].cost, 0),
    items: rows.reduce((n, r) => n + r[k].items, 0),
    costMissing: rows.reduce((n, r) => n + r[k].costMissing, 0),
  });
  return {
    ...base,
    rolledUp: rows.length,
    invoiced: rows.reduce((n, r) => n + r.invoiced, 0),
    invoices: rows.reduce((n, r) => n + r.invoices, 0),
    labor: {
      revenue: rows.reduce((n, r) => n + r.labor.revenue, 0),
      cost: rows.reduce((n, r) => n + r.labor.cost, 0),
      items: rows.reduce((n, r) => n + r.labor.items, 0),
      costMissing: rows.reduce((n, r) => n + r.labor.costMissing, 0),
      hours: rows.reduce((n, r) => n + r.labor.hours, 0),
      billableHours: rows.reduce((n, r) => n + r.labor.billableHours, 0),
    },
    recurring: part('recurring'),
    products: part('products'),
  };
}

/**
 * Reports › SDF — profitability for a company and the companies under it in
 * Autotask. The gross margin is what was invoiced less what it cost; posted
 * billing items sit beside it, because they are the only revenue that can be
 * split into labor, recurring and products. Every figure has its own column
 * and the formulas are spelled out under the table; see routes/reports.ts for
 * where each comes from.
 *
 * The picker lists the ACTIVE companies with a “(” in their name — the
 * franchise accounts, e.g. “CMIT Solutions of Alexandria (1247)”. Tick one or
 * several (or all) and Compare; with several, each is one row with the
 * companies under it rolled in, so they can be set against each other.
 */
export function SdfReportPage() {
  const [picked, setPicked] = useState<{ id: number; name: string }[]>([]);
  const [choosing, setChoosing] = useState(true);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const companies = useHeldResource<{ id: number; name: string }[]>('/reports/sdf/companies');
  const eligible = useMemo(() => (companies.data ?? []).filter((c) => c.name.includes('(')), [companies.data]);
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? eligible.filter((c) => c.name.toLowerCase().includes(q)) : eligible;
  }, [eligible, search]);
  const toggle = (id: number) =>
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const compare = () => {
    setPicked(eligible.filter((c) => checked.has(c.id)));
    setChoosing(false);
  };

  const [range, setRange] = useState<Range>('ytd');
  const [customFrom, setCustomFrom] = useState(rangeDays('ytd')[0]);
  const [customTo, setCustomTo] = useState(todayLocal());
  const [fromDay, toDay] = range === 'custom' ? [customFrom, customTo] : rangeDays(range);
  const from = new Date(`${fromDay}T00:00:00`);
  const to = new Date(`${toDay}T00:00:00`);
  to.setDate(to.getDate() + 1);
  const valid = picked.length > 0 && fromDay && toDay && fromDay <= toDay;

  const data = useHeldResource<SdfPayload>(
    valid
      ? `/reports/sdf?${new URLSearchParams({ parentIds: picked.map((p) => p.id).join(','), from: from.toISOString(), to: to.toISOString() })}`
      : null,
  );
  const comparing = (data.data?.parents.length ?? 0) > 1;
  const title = comparing ? `${data.data!.parents.length} companies` : (data.data?.parents[0]?.name ?? '');

  const [sort, setSort] = useState<'margin' | 'revenue' | 'name' | 'marginPct'>('margin');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [rollUpPicks, setRollUpPicks] = useState(true);
  const rows = useMemo(() => {
    const all = data.data?.rows ?? [];
    const byPick = comparing && rollUpPicks
      ? all.filter((r) => r.isParent).map((p) => rollUp(p, all.filter((r) => r.parentId === p.id)))
      : all;
    const list = byPick.map((r) => ({ ...r, f: figures(r) }));
    const shown = hideEmpty ? list.filter((r) => r.f.invoiced !== 0 || r.f.posted !== 0 || r.f.cost !== 0 || r.isParent) : list;
    return shown.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'revenue') return b.f.invoiced - a.f.invoiced;
      if (sort === 'marginPct') return (b.f.invoiced ? b.f.margin / b.f.invoiced : -Infinity) - (a.f.invoiced ? a.f.margin / a.f.invoiced : -Infinity);
      return b.f.margin - a.f.margin;
    });
  }, [data.data, sort, hideEmpty, comparing, rollUpPicks]);
  const parentName = (id: number) => data.data?.parents.find((p) => p.id === id)?.name ?? '';
  const companyName = (id: number) => data.data?.rows.find((r) => r.id === id)?.name ?? '';
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const total = useMemo(() => {
    const all = data.data?.rows ?? [];
    const sum = (k: 'labor' | 'recurring' | 'products', f: 'revenue' | 'cost') => all.reduce((n, r) => n + r[k][f], 0);
    const t = {
      labor: { revenue: sum('labor', 'revenue'), cost: sum('labor', 'cost'), hours: all.reduce((n, r) => n + r.labor.hours, 0), billableHours: all.reduce((n, r) => n + r.labor.billableHours, 0), items: 0, costMissing: 0 },
      recurring: { revenue: sum('recurring', 'revenue'), cost: sum('recurring', 'cost'), items: 0, costMissing: 0 },
      products: { revenue: sum('products', 'revenue'), cost: sum('products', 'cost'), items: 0, costMissing: 0 },
      invoiced: data.data?.invoiced ?? 0,
      invoices: data.data?.invoices ?? 0,
    };
    return { ...t, f: figures(t) };
  }, [data.data]);

  const exportCsv = () =>
    downloadCsv(`sdf_profitability_${fileStamp()}.csv`, [
      ['Client', 'Labor revenue', 'Labor cost', 'Labor margin', 'Hours', 'Billable hours', 'Recurring revenue', 'Recurring cost', 'Products & expenses revenue', 'Products & expenses cost', 'Posted revenue', 'Invoiced', 'Invoices', 'Total cost', 'Gross margin (invoiced − cost)', 'Gross margin %', 'Margin on posted', 'Invoiced per hour', 'Labor cost per hour'],
      ...[...rows, { name: 'TOTAL', ...total }].map((r) => {
        const f = 'f' in r ? r.f : figures(r);
        return [
          r.name, r.labor.revenue, r.labor.cost, f.laborMargin, r.labor.hours, r.labor.billableHours, r.recurring.revenue, r.recurring.cost,
          r.products.revenue, r.products.cost, f.posted, f.invoiced, r.invoices, f.cost, f.margin,
          f.invoiced ? Math.round((f.margin / f.invoiced) * 1000) / 10 : '',
          f.postedMargin,
          r.labor.hours ? Math.round((f.invoiced / r.labor.hours) * 100) / 100 : '',
          r.labor.hours ? Math.round((r.labor.cost / r.labor.hours) * 100) / 100 : '',
        ];
      }),
    ]);

  return (
    <div className="sdf-page">
      <PageHead
        title="SDF"
        lede="Profitability for a company and every company under it in Autotask — what each client was invoiced, what it cost and the gross margin between them, with posted billing beside it and every calculation shown. Pick an active company to begin."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-4)' }}>
            <Field label="Company">
              {picked.length > 0 ? (
                <div className="row">
                  <span className="strong">{picked.length === 1 ? picked[0]!.name : `${picked.length} companies`}</span>
                  {!choosing && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setChecked(new Set(picked.map((p) => p.id)));
                        setChoosing(true);
                      }}
                    >
                      Change
                    </button>
                  )}
                </div>
              ) : (
                <span className="muted small">None picked — tick one or more below</span>
              )}
            </Field>
            <Field label="Billed">
              <Segmented label="Billed" options={RANGES} value={range} onChange={setRange} />
            </Field>
            {range === 'custom' && (
              <>
                <Field label="From">
                  <input className="input" type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
                </Field>
                <Field label="To">
                  <input className="input" type="date" value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} />
                </Field>
              </>
            )}
            <button type="button" className="btn btn-ghost btn-sm" disabled={!data.data || data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            {data.fetchedAt && (
              <span className="xs muted">
                As of {new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>

          {choosing && (
            <div className="mt-4">
              {companies.error && <ErrorBanner message={companies.error} />}
              {companies.loading && <Loading label="Reading active companies from Autotask" />}
              {companies.data && (
                <>
                  <input
                    className="input"
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Type to filter ${eligible.length.toLocaleString()} active companies with “(” in the name`}
                  />
                  <div className="row wrap mt-2">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={matches.length === 0}
                      onClick={() => setChecked((s) => new Set([...s, ...matches.map((c) => c.id)]))}
                    >
                      {search ? `Select all ${matches.length} shown` : `Select all ${matches.length}`}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={checked.size === 0} onClick={() => setChecked(new Set())}>
                      Clear
                    </button>
                    <span className="xs muted grow">{checked.size} selected</span>
                    {picked.length > 0 && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setChoosing(false)}>
                        Cancel
                      </button>
                    )}
                    <button type="button" className="btn btn-primary btn-sm" disabled={checked.size === 0} onClick={compare}>
                      {checked.size > 1 ? `Compare ${checked.size}` : 'Show'}
                    </button>
                  </div>
                  <div className="sdf-pick mt-2">
                    {matches.length === 0 && <p className="xs muted">No active company with “(” contains “{search}”.</p>}
                    {matches.map((c) => (
                      <label key={c.id} className="sdf-pick-row">
                        <input type="checkbox" checked={checked.has(c.id)} onChange={() => toggle(c.id)} />
                        {c.name}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && <Loading label="Reading posted billing from Autotask" />}

      {data.data && (
        <>
          {data.data.costMissing > 0 && (
            <div className="card mb-4">
              <div className="card-body small" style={{ color: 'var(--status-danger)' }}>
                <strong>Costs are missing on {num(data.data.costMissing)} of {num(data.data.items)} billing items.</strong> Autotask
                returns no cost to an API user whose security level cannot read costs, so cost and margin below are
                understated for those items. Give the Autotask API user a security level that can read costs (not “Can’t
                Read Costs”).
              </div>
            </div>
          )}

          <div className="grid grid-4 mb-4">
            <Stat
              label="Invoiced"
              value={money(data.data.invoiced)}
              sub={`${money(total.f.posted)} posted revenue`}
              foot={`${shortDate(fromDay)} – ${shortDate(toDay)} · ${num(data.data.rows.length)} companies · ${num(data.data.invoices)} invoice${data.data.invoices === 1 ? '' : 's'}`}
            />
            <Stat label="Cost" value={money(total.f.cost)} foot={`labor ${money(total.labor.cost)} · products ${money(total.products.cost)}`} />
            <Stat
              label="Gross margin"
              value={money(total.f.margin)}
              foot={`invoiced − cost · ${pctOf(total.f.margin, total.f.invoiced)} of invoiced`}
              flagged={total.f.margin < 0}
            />
            <Stat label="Hours" value={num(total.labor.hours, 1)} foot={`${perHour(total.f.invoiced, total.labor.hours)} invoiced per hour`} />
          </div>

          <section className="card mb-4">
            <div className="card-head">
              <div className="grow">
                <h3>Profitability by client — {title}</h3>
                <p className="sub">
                  {rows.length} shown ·{' '}
                  {comparing && rollUpPicks
                    ? 'each row is a picked company with every company under it added in'
                    : comparing
                      ? 'each picked company’s own billing is the row marked “parent”'
                      : `${title}’s own billing is the row marked “parent”`}
                </p>
              </div>
              <div className="row wrap">
                {comparing && (
                  <label className="checkbox small">
                    <input type="checkbox" checked={rollUpPicks} onChange={(e) => setRollUpPicks(e.target.checked)} />
                    One row per picked company
                  </label>
                )}
                <select className="select" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
                  <option value="margin">Sort: margin $</option>
                  <option value="marginPct">Sort: margin %</option>
                  <option value="revenue">Sort: invoiced</option>
                  <option value="name">Sort: name</option>
                </select>
                <label className="checkbox small">
                  <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
                  Hide clients with nothing billed
                </label>
                <button type="button" className="btn btn-secondary btn-sm" onClick={exportCsv}>
                  Export CSV
                </button>
              </div>
            </div>
            <div className="table-wrap">
              <table className="table sdf-table">
                <thead>
                  <tr>
                    <th rowSpan={2}>Client</th>
                    <th colSpan={4} className="sdf-group">Labor</th>
                    <th colSpan={2} className="sdf-group">Recurring</th>
                    <th colSpan={2} className="sdf-group">Products &amp; expenses</th>
                    <th colSpan={6} className="sdf-group">Total</th>
                  </tr>
                  <tr>
                    <th className="num">Revenue</th>
                    <th className="num">Cost</th>
                    <th className="num">Margin</th>
                    <th className="num">Hours</th>
                    <th className="num">Revenue</th>
                    <th className="num">Cost</th>
                    <th className="num">Revenue</th>
                    <th className="num">Cost</th>
                    <th className="num">Posted</th>
                    <th className="num">Invoiced</th>
                    <th className="num">Cost</th>
                    <th className="num">Gross margin</th>
                    <th className="num">Margin %</th>
                    <th className="num">Inv / hr</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const key = `${r.id}:${r.rolledUp !== undefined ? 'all' : 'own'}`;
                    const open = expanded.has(key);
                    return (
                    <Fragment key={key}>
                    <tr className={open ? 'sdf-open' : undefined}>
                      <td>
                        <button type="button" className="sdf-expand" aria-expanded={open} onClick={() => toggleExpanded(key)}>
                          <span className="sdf-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                          <span className="strong">{r.name}</span>
                        </button>
                        {r.rolledUp !== undefined ? (
                          r.rolledUp > 1 && <span className="xs muted"> · {r.rolledUp} companies</span>
                        ) : r.isParent ? (
                          <span className="xs muted"> · parent</span>
                        ) : (
                          comparing && <span className="xs muted"> · under {parentName(r.parentId)}</span>
                        )}
                        {!r.active && <span className="xs muted"> · inactive</span>}
                      </td>
                      <td className="num">{money(r.labor.revenue)}</td>
                      <td className="num">{money(r.labor.cost)}</td>
                      <td className="num">{money(r.f.laborMargin)}</td>
                      <td className="num">{num(r.labor.hours, 1)}</td>
                      <td className="num">{money(r.recurring.revenue)}</td>
                      <td className="num">{money(r.recurring.cost)}</td>
                      <td className="num">{money(r.products.revenue)}</td>
                      <td className="num">{money(r.products.cost)}</td>
                      <td className="num">{money(r.f.posted)}</td>
                      <td className="num strong">{money(r.f.invoiced)}</td>
                      <td className="num">{money(r.f.cost)}</td>
                      <td className="num strong" style={r.f.margin < 0 ? { color: 'var(--status-danger)' } : undefined}>
                        {money(r.f.margin)}
                      </td>
                      <td className="num">{pctOf(r.f.margin, r.f.invoiced)}</td>
                      <td className="num">{perHour(r.f.invoiced, r.labor.hours)}</td>
                    </tr>
                    {open && (
                      <tr className="sdf-items-row">
                        <td colSpan={15}>
                          <SdfItems
                            companyIds={r.rolledUp !== undefined ? data.data!.rows.filter((c) => c.parentId === r.id).map((c) => c.id) : [r.id]}
                            companyName={r.rolledUp !== undefined && r.rolledUp > 1 ? companyName : undefined}
                            from={from.toISOString()}
                            to={to.toISOString()}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                  <tr className="sdf-total">
                    <td>Total</td>
                    <td className="num">{money(total.labor.revenue)}</td>
                    <td className="num">{money(total.labor.cost)}</td>
                    <td className="num">{money(total.f.laborMargin)}</td>
                    <td className="num">{num(total.labor.hours, 1)}</td>
                    <td className="num">{money(total.recurring.revenue)}</td>
                    <td className="num">{money(total.recurring.cost)}</td>
                    <td className="num">{money(total.products.revenue)}</td>
                    <td className="num">{money(total.products.cost)}</td>
                    <td className="num">{money(total.f.posted)}</td>
                    <td className="num strong">{money(total.f.invoiced)}</td>
                    <td className="num">{money(total.f.cost)}</td>
                    <td className="num strong">{money(total.f.margin)}</td>
                    <td className="num">{pctOf(total.f.margin, total.f.invoiced)}</td>
                    <td className="num">{perHour(total.f.invoiced, total.labor.hours)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h3>How each figure is calculated</h3>
            </div>
            <div className="card-body small">
              <p className="mb-2">
                From Autotask’s <strong>invoices</strong> dated in the period — which is what the gross margin runs on —
                and its <strong>posted billing items</strong> dated in the period, the time, products and recurring
                charges that have been approved and posted, which is what the revenue and cost columns are split from.
                Both are for {comparing ? 'each picked company' : title} and every company whose parent is{' '}
                {comparing ? 'one of them' : title}. Nothing that is still waiting to be approved and posted is counted,
                and a client with no invoice dated in the period shows its cost as a loss.
              </p>
              <ul className="sdf-formulas">
                <li><strong>Revenue</strong> (each column) = the sum of what each billing item was posted at (Autotask “extended price”).</li>
                <li><strong>Cost</strong> (each column) = the sum of Autotask’s “our cost” on those items — for labor, the hours at each technician’s internal cost; for products and expenses, what they cost.</li>
                <li><strong>Labor</strong> = billing items of type Labor and Labor Adjustment. <strong>Hours</strong> = their hours to bill ({num(total.labor.billableHours, 1)} of {num(total.labor.hours, 1)} billable in total).</li>
                <li><strong>Labor margin</strong> = labor revenue − labor cost.</li>
                <li><strong>Recurring</strong> = Recurring Service/Bundle, Setup Fee, Milestone and Subscription items.</li>
                <li><strong>Products &amp; expenses</strong> = Cost and Expense items (hardware, software, materials, expenses).</li>
                <li><strong>Posted</strong> = labor + recurring + products &amp; expenses revenue — the only revenue that can be split by what was sold, which is what the columns to the left do.</li>
                <li><strong>Total cost</strong> = labor + recurring + products &amp; expenses cost.</li>
                <li>
                  <strong>Invoiced</strong> = the Autotask invoices dated in the period for these companies, voided ones
                  left out, at the invoice total — with tax. It is dated by the invoice rather than by the work, so it
                  will not tie exactly to posted revenue; the two are shown side by side on purpose.
                </li>
                <li><strong>Gross margin</strong> = invoiced − total cost — what was billed out, less what it cost us. <strong>Margin %</strong> = gross margin ÷ invoiced.</li>
                <li><strong>Inv / hr</strong> = invoiced ÷ labor hours — what each hour spent on the client brought in, recurring included.</li>
              </ul>
              <p className="xs muted mt-2">
                {num(data.data.items)} billing items read. Overheads, tools licensed per seat but not billed through
                Autotask, and time not yet posted are not in these figures.
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
